import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Chart from 'react-apexcharts';
import { Card, Text, Metric } from '@tremor/react';

const apiUrlSeleccion = process.env.REACT_APP_API_PEDIDOS;

const CardGripMapSemanal = ({ desde, hasta, sedeId }) => {
  const [datos, setDatos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const obtenerDatos = async () => {
      try {
        setCargando(true);
        setError(null);
        // Construir query string con fechas si están presentes
        let query = "";
        if (desde) query += `desde=${desde}`;
        if (hasta) query += `${query ? "&" : ""}hasta=${hasta}`;
        const url = query ? `${apiUrlSeleccion}?${query}` : apiUrlSeleccion;
        const res = await axios.get(url);
        console.log('Datos semanal cargados:', res.data);
        
        // Filtrar por sede si se seleccionó una
        let datosFiltrados = res.data || [];
        if (sedeId && sedeId !== 'todas') {
          datosFiltrados = datosFiltrados.filter(p => p.sede_id === sedeId);
        }
        
        setDatos(datosFiltrados);
      } catch (err) {
        console.error('Error al cargar datos:', err);
        setError(err.message);
      } finally {
        setCargando(false);
      }
    };

    obtenerDatos();
  }, [desde, hasta, sedeId]);

  // Agrupación por día y filtro solo próxima semana
  const cantidadPorTipoYDia = {};
  const tiposDeMenu = new Set(); // Para detectar dinámicamente los tipos
  
  // Calcular rango de próxima semana (lunes a viernes)
  const hoy = new Date();
  const diaSemana = hoy.getDay();
  // Lunes = 1, Domingo = 0
  const diasHastaLunes = (diaSemana === 0 ? 1 : 8 - diaSemana);
  const inicioProximaSemana = new Date(hoy);
  inicioProximaSemana.setDate(hoy.getDate() + diasHastaLunes);
  inicioProximaSemana.setHours(0, 0, 0, 0);
  const finProximaSemana = new Date(inicioProximaSemana);
  finProximaSemana.setDate(inicioProximaSemana.getDate() + 4); // Solo lunes a viernes
  finProximaSemana.setHours(23, 59, 59, 999);

  datos.forEach((pedido) => {
    const fecha = pedido.fecha;
    const fechaObj = new Date(fecha);
    const tipo = pedido.tipo || 'Sin tipo';
    
    // Agregar tipo al Set
    tiposDeMenu.add(tipo);
    
    // Solo fechas estrictamente de la próxima semana (no incluir actuales ni anteriores)
    if (fechaObj >= inicioProximaSemana && fechaObj <= finProximaSemana) {
      if (!cantidadPorTipoYDia[fecha]) {
        cantidadPorTipoYDia[fecha] = {};
      }
      if (!cantidadPorTipoYDia[fecha][tipo]) {
        cantidadPorTipoYDia[fecha][tipo] = 0;
      }
      cantidadPorTipoYDia[fecha][tipo]++;
    }
  });
  
  // Convertir Set a Array para usarlo
  const tiposArray = Array.from(tiposDeMenu);

  // Preparar datos para el gráfico de línea
  // Filtrar fechas dentro del rango seleccionado
  const fechasOrdenadas = Object.keys(cantidadPorTipoYDia)
    .filter(fecha => {
      if (!desde && !hasta) return true;
      const fechaObj = new Date(fecha);
      const desdeObj = desde ? new Date(desde) : null;
      const hastaObj = hasta ? new Date(hasta) : null;
      if (desdeObj && fechaObj < desdeObj) return false;
      if (hastaObj && fechaObj > hastaObj) return false;
      return true;
    })
    .sort();

  const labels = fechasOrdenadas.map((fecha) =>
    new Date(fecha + 'T00:00').toLocaleDateString('es-ES', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    })
  );

  // Generar series dinámicamente para cada tipo de menú
  const seriesLine = tiposArray.map(tipo => ({
    name: tipo,
    data: fechasOrdenadas.map(fecha => cantidadPorTipoYDia[fecha]?.[tipo] || 0)
  }));


  return (
    <div className="mt-6">
      {cargando && <p className="text-white mb-4">Cargando datos...</p>}
      {error && <p className="text-red-500 mb-4">Error: {error}</p>}
      {!cargando && datos.length === 0 && <p className="text-gray-400 mb-4">No hay datos disponibles</p>}

      {/* Tarjetas por día */}
      <div className="flex flex-wrap gap-6 mb-10">
        {fechasOrdenadas.map((fecha) => {
          const fechaFormateada = new Date(fecha + 'T00:00').toLocaleDateString('es-ES', {
            weekday: 'long',
            month: 'long',
            day: 'numeric',
          });

          return (
            <Card
              key={fecha}
              className="w-[260px] bg-slate-900 text-white shadow-xl hover:shadow-2xl transition-all rounded-xl"
            >
              <Metric className="text-xl mb-2">
                {fechaFormateada.charAt(0).toUpperCase() + fechaFormateada.slice(1)}
              </Metric>
              {tiposArray.map(tipo => (
                <Text key={tipo}>
                  {tipo}: {cantidadPorTipoYDia[fecha]?.[tipo] || 0}
                </Text>
              ))}
            </Card>
          );
        })}
      </div>

      {/* Gráfico de línea por día */}
      {/* Eliminada línea duplicada de Card */}
      <Card className="p-4 bg-slate-900 text-white rounded-xl shadow-xl">
        <Metric className="mb-4">Evolución diaria de pedidos</Metric>
        <Chart
          options={{
            chart: {
              type: 'bar',
              foreColor: '#fff',
              toolbar: { show: false },
            },
            plotOptions: {
              bar: {
                horizontal: false,
                columnWidth: '60%',
              },
            },
            colors: ['#3b82f6', '#10b981', '#f59e0b'], // azul, verde, dorado
            dataLabels: {
              enabled: true,
              style: {
                colors: ['#fff'],
              },
            },
            xaxis: {
              categories: labels,
              labels: {
                style: { colors: '#fff' },
              },
            },
            yaxis: {
              labels: {
                style: { colors: '#fff' },
              },
            },
            legend: {
              position: 'top',
              labels: {
                colors: ['#fff'],
              },
            },
            tooltip: {
              theme: 'dark',
            },
          }}
          series={seriesLine}
          type="bar"
          height={400}
        />
      </Card>

    </div>
  );
};

export default CardGripMapSemanal;


